<template>
    <div class="enrollment info-box is-card">
        <div class="box-main">
            <div class="box-icon is-hidden-touch">
                <i class="fad fa-chalkboard-teacher has-text-primary fa-2x"></i>
            </div>
            <div class="box-body">
                <div class="card-title">
                    <i class="fad fa-chalkboard-teacher has-text-primary is-hidden-desktop mr-2"></i>{{ enrollment.custom_name || enrollment.courses.title }}
                </div>
                <div class="card-meta" v-if="enrollment.custom_name">{{ enrollment.courses.title }}</div>
                <!-- Provider matters on the record of a finished course (who
                     awarded it); it's noise while the course is in progress -->
                <div class="card-meta" v-if="enrollment.completed_at && enrollment.courses.organizations">{{ enrollment.courses.organizations.title }}</div>

                <div class="card-chips mt-2" v-if="chips.length">
                    <status-chip v-for="chip in chips" :key="chip.label" :label="chip.label" :type="chip.type" :icon="chip.icon"/>
                </div>

                <!-- Live enrollment: progress bars (dates live in the footer) -->
                <template v-if="isLive">
                    <div class="card-progress mt-3" v-if="enrollment.max_hours > 0">
                        <b-progress type="is-info" size="" :value="hoursPct" class="enroll-progress"/>
                        <div class="card-meta">{{ enrollment.total_logged_hours }} of {{ enrollment.max_hours }} hours logged ({{ hoursPct.toFixed(0) }}%)</div>
                    </div>
                    <div class="card-progress mt-3" v-if="enrollment.lessons > 0">
                        <b-progress type="is-primary" size="" :value="lessonsPct" class="enroll-progress"/>
                        <div class="card-meta">{{ enrollment.lessons_completed }} of {{ enrollment.lessons }} lessons completed ({{ lessonsPct.toFixed(0) }}%)</div>
                    </div>
                </template>
                <div class="card-meta mt-3" v-else-if="enrollment.max_hours > 0">
                    <i class="far fa-clock mr-1"></i>{{ enrollment.max_hours }}-hour enrollment &middot; {{ enrollment.total_logged_hours }} hours logged
                </div>
            </div>
        </div>

        <!-- Alerts get their own bordered line above the footer -->
        <div class="box-alert" v-if="note"><i class="fas fa-fw fa-circle-info mr-2"></i>{{ note }}</div>

        <!-- Footer: plain status/date info on the left, actions on the right -->
        <div class="box-actions" v-if="info || buttons.length">
            <div class="box-lead box-info" v-if="info"><i :class="['far', 'fa-fw', info.icon, 'mr-2']"></i>{{ info.text }}</div>
            <div class="box-buttons">
                <template v-for="(btn, i) in buttons">
                    <b-button v-if="btn.href" :key="i + '-' + btn.label" tag="a" :href="btn.href" target="_blank" rel="noopener" type="is-primary" :outlined="btn.outlined" size="is-small"><i :class="['fas', btn.icon, 'mr-2']"></i>{{ btn.label }}<i class="fas fa-arrow-up-right-from-square ml-2"></i></b-button>
                    <b-button v-else-if="btn.disabled" :key="i + '-' + btn.label" type="is-primary" size="is-small" disabled><i :class="['fas', btn.icon, 'mr-2']"></i>{{ btn.label }}</b-button>
                    <b-button v-else :key="i + '-' + btn.label" tag="router-link" :to="btn.to" type="is-primary" :outlined="btn.outlined" size="is-small"><i :class="['fas', btn.icon, 'mr-2']"></i>{{ btn.label }}</b-button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from "moment";
    import StatusChip from "./framework/StatusChip";
    import {timeAgo} from "../utils/dates";
    import {NEXT_STEP_LABELS, NEXT_STEP_ICONS, nextStepNote} from "../constants/attention";

    /**
     * Course Enrollments card. Same anatomy as the dashboard continue tile
     * (global .info-box.is-card): icon column, body with chips + progress,
     * footer CTA area whose note + buttons follow the enrollment's state
     * and the API's next_step. Replaces the old five-alert card.
     */
    export default {
        name: "Enrollment",
        components: {StatusChip},
        props: {
            enrollment: {
                required: true
            }
        },
        computed: {
            // Same rule as the API's enrollments/lock.js isExpired: a completed
            // enrollment is frozen, not expired.
            isExpired() {
                if (typeof this.enrollment.is_expired === 'boolean') return this.enrollment.is_expired && !this.enrollment.completed_at;
                if (!this.enrollment.expires_on || this.enrollment.completed_at) return false;
                return moment.utc(this.enrollment.expires_on).isBefore(moment(), 'day');
            },
            isLive() {
                return !this.enrollment.completed_at && !this.enrollment.reactivated_to_enrollment_id && !this.isExpired;
            },
            hoursPct() {
                if (!this.enrollment.max_hours) return 0;
                return Math.min((this.enrollment.total_logged_hours / this.enrollment.max_hours) * 100, 100);
            },
            lessonsPct() {
                return this.enrollment.lessons ? (this.enrollment.lessons_completed / this.enrollment.lessons) * 100 : 0;
            },
            chips() {
                const e = this.enrollment;
                const out = [];
                // No chip for completed / reactivated: the section heading and
                // the "Completed on…" line already say it
                if (e.reactivated_to_enrollment_id || e.completed_at) return out;
                if (this.isExpired) out.push({label: 'Expired', type: 'danger', icon: 'fa-calendar-xmark'});
                else if (e.payment_locked) out.push({label: 'Locked', type: 'warning', icon: 'fa-lock'});
                else if (e.days_to_expiry !== null && e.days_to_expiry !== undefined && e.days_to_expiry <= 30)
                    out.push({label: e.days_to_expiry <= 0 ? 'Expires today' : `Expires in ${e.days_to_expiry} day${e.days_to_expiry === 1 ? '' : 's'}`, type: 'warning', icon: 'fa-calendar-clock'});
                return out;
            },
            // The footer's information slot: where this enrollment stands in time
            info() {
                const e = this.enrollment;
                if (e.reactivated_to_enrollment_id) {
                    return {icon: 'fa-rotate', text: 'Reactivated into a new enrollment' + (e.reactivated_at ? ' on ' + moment(e.reactivated_at).format('MMMM Do, YYYY') : '')};
                }
                if (e.completed_at) return {icon: 'fa-check-circle', text: 'Completed ' + moment(e.completed_at).format('MMMM Do, YYYY')};
                if (this.isExpired) return {icon: 'fa-calendar-xmark', text: 'Expired ' + this.formatExpireDate(e.expires_on)};
                const parts = [];
                if (e.expires_on) parts.push('Expires ' + this.formatExpireDate(e.expires_on));
                if (e.last_activity_at) parts.push('Last active ' + timeAgo(e.last_activity_at).toLowerCase());
                return parts.length ? {icon: 'fa-clock', text: parts.join(' · ')} : null;
            },
            // The alert line: the one thing worth saying about this state
            note() {
                const e = this.enrollment;
                if (e.reactivated_to_enrollment_id) return null;
                if (e.completed_at) return e.isIncompleteApps ? 'You have applications or forms for this course that still need to be completed.' : null;
                if (this.isExpired) return 'This enrollment has expired. Reactivate it to pick up where you left off.';
                if (e.payment_locked) return 'This course is part of a payment plan with The Payment Option. Complete the plan setup to unlock it.';
                return nextStepNote(e);
            },
            buttons() {
                const e = this.enrollment;
                const out = [];
                if (e.reactivated_to_enrollment_id) {
                    out.push({label: 'Open New Enrollment', icon: 'fa-arrow-right', to: '/course/' + e.reactivated_to_enrollment_id, outlined: true});
                    return out;
                }
                if (e.completed_at) {
                    if (e.courses.new_course) {
                        out.push({label: 'Review Course', icon: 'fa-eye', to: '/course/' + e.id, outlined: true});
                        out.push({label: 'Certificates & Forms', icon: 'fa-file-certificate', to: {name: 'course-applications', params: {enrollmentId: e.id}}, outlined: false});
                    }
                    return out;
                }
                if (this.isExpired) {
                    out.push({label: 'Reactivate', icon: 'fa-redo', to: {name: 'reactivation-checkout'}, outlined: false});
                    return out;
                }
                if (e.payment_locked) {
                    if (e.payment_agreement_url) out.push({label: 'Complete Setup', icon: 'fa-file-signature', href: e.payment_agreement_url});
                    else out.push({label: 'Locked', icon: 'fa-lock', disabled: true});
                    return out;
                }
                // Live. Secondary (outlined) actions first, the next step last
                // as the primary. The course itself and the study log are
                // always reachable even when the next step is something else
                // (a reflection, the study plan, an application…).
                const step = e.next_step;
                const selfDirected = e.courses && e.courses.type === 'self_directed';
                // No next step known yet (dashboard rows before the feed
                // lands) reads as a plain "Open Course" primary
                const courseIsPrimary = !step || step === 'lesson' || step === 'review' || step === 'confirm_complete';
                if (!courseIsPrimary) {
                    out.push({label: 'Open Course', icon: 'fa-chalkboard-teacher', to: '/course/' + e.id, outlined: true});
                }
                // Log Time only pairs with a plain "Open Course": if something
                // is blocking (reflection, study plan, application), logging
                // time is not the thing to do (Derek)
                if (courseIsPrimary && selfDirected && e.max_hours > 0) {
                    out.push({label: 'Log Time', icon: 'fa-clock', to: {name: 'study-log', params: {enrollmentId: e.id}}, outlined: true});
                }
                if (e.application_readiness && (e.isIncompleteApps || e.hasIncompleteOptionalApps) && step !== 'apply') {
                    out.push({label: 'Certificates & Forms', icon: 'fa-file-certificate', to: {name: 'course-applications', params: {enrollmentId: e.id}}, outlined: true});
                }
                out.push({
                    // "Continue" reads wrong on a course nobody has opened yet
                    label: step === 'lesson' ? 'Open Course' : (NEXT_STEP_LABELS[step] || 'Open Course'),
                    icon: step === 'lesson' ? 'fa-chalkboard-teacher' : (NEXT_STEP_ICONS[step] || 'fa-pencil'),
                    to: e.next_route || ('/course/' + e.id),
                    outlined: false,
                });
                return out;
            }
        },
        methods: {
            // expires_on is a DATE column — format in UTC so the day never
            // shifts backward in western timezones
            formatExpireDate(date) {
                return moment.utc(date).format('MMMM Do, YYYY');
            }
        }
    };
</script>

<style lang="scss" scoped>
    // Layout comes from the global .info-box.is-card (assets/scss/_cta.scss)
    .card-title {
        font-weight: 700;
        line-height: 1.3;
    }

    .card-meta {
        font-size: 0.85rem;
        color: #7a7a7a;
    }

    .card-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
    }

    .card-progress {
        width: 80%;
    }
</style>
